import React, { memo, useContext, useEffect, useReducer } from 'react';
import './css/GameWidget3.css';
import { StyleSheet, css } from 'aphrodite';
import PropTypes from 'prop-types';
import { NavLink, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Tooltip from '@material-ui/core/Tooltip';
import { isValidObject, isValidString } from '../../../../catalog/Validity';
import { context } from '../../../../globalState/GlobalState';
import { getFullDateStringFromDate, getLocalTimeFromDatabaseTime, getTimeInAMPMFromDate } from '../../../../catalog/DateTime';
import DemoLogo from '../../../../assets/demoOpponentTeam.png';
import { opponentPointsFromGame, ourPointsFromGame } from '../../../../catalog/stats/rugby/RugbyGameStats';
import { opponentGoalsFromGame as opponentIceHockeyGoalsFromGame, ourGoalsFromGame as ourIceHockeyGoalsFromGame } from '../../../../catalog/stats/iceHockey/IceHockeyGameStats';
import { opponentGoalsFromGame as opponentLacrosseGoalsFromGame, ourGoalsFromGame as ourLacrosseGoalsFromGame } from '../../../../catalog/stats/lacrosse/LacrosseGameStats';
import { SportsType } from '../../../../catalog/enums/SportsType';
import { fetchTicketsByEventAPI } from '../../../apiCalls/TicketAPICalls';
import Button1 from '../../buttons/Button1';
import { showGameTickets } from '../../../../catalog/Store';

const initialState = {
    tickets: [],
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchTickets':
            return { ...state, tickets: action.tickets };
        default:
            return initialState;
    }
}

function GameWidget3(props) {
    const { id, game, opponentName, opponentLogo, scheduled, homeGame, status, timeOfGame, venue, primaryBackgroundColor, smaller } = props;
    const [state, setState] = useReducer(reducer, initialState);
    const { globalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();

    useEffect(() => {
        async function fetchTickets() {
            if (isValidObject(globalState.accountId) && isValidString(id)) {
                await fetchTicketsByEventAPI(globalState.accountId, id, setState, enqueueSnackbar);
            }
        }

        fetchTickets();
    }, [globalState.accountId, id]);

    const styles = StyleSheet.create({
        container: {
            backgroundColor: primaryBackgroundColor && isValidString(globalState.websiteStyles.colors.primaryColor) ? globalState.websiteStyles.colors.primaryColor : '#FFFFFF',
            color: primaryBackgroundColor && isValidString(globalState.websiteStyles.colors.fontColor) ? globalState.websiteStyles.colors.fontColor : '#000000',
            // backgroundImage: `linear-gradient(to bottom, ${globalState.websiteStyles.colors.primaryColor} 0%, ${isValidString(globalState.websiteStyles.colors.secondaryColor) ? globalState.websiteStyles.colors.secondaryColor : '#181e29'} 60%)`,
        },
    });

    const getOurFinalScore = () => {
        if (status === 'postponed' || status === 'cancelled' || status === 'tba' || status === 'tbat' || status === 'scheduled') {
            return '';
        }
        if (globalState.sportsType === SportsType.WOMENSICEHOCKEY || globalState.sportsType === SportsType.MENSICEHOCKEY) {
            return ourIceHockeyGoalsFromGame(game);
        }
        if (globalState.sportsType === SportsType.WOMENSRUGBY || globalState.sportsType === SportsType.MENSRUGBY) {
            return ourPointsFromGame(game);
        }
        if (globalState.sportsType === SportsType.WOMENSLACROSSE || globalState.sportsType === SportsType.MENSLACROSSE) {
            return ourLacrosseGoalsFromGame(game);
        }
        return '';
    };

    const getOpponentFinalScore = () => {
        if (status === 'postponed' || status === 'cancelled' || status === 'tba' || status === 'tbat' || status === 'scheduled') {
            return '';
        }
        if (globalState.sportsType === SportsType.WOMENSICEHOCKEY || globalState.sportsType === SportsType.MENSICEHOCKEY) {
            return opponentIceHockeyGoalsFromGame(game);
        }
        if (globalState.sportsType === SportsType.WOMENSRUGBY || globalState.sportsType === SportsType.MENSRUGBY) {
            return opponentPointsFromGame(game);
        }
        if (globalState.sportsType === SportsType.WOMENSLACROSSE || globalState.sportsType === SportsType.MENSLACROSSE) {
            return opponentLacrosseGoalsFromGame(game);
        }
        return '';
    };

    const getMiddleText = () => {
        if (status === 'postponed') {
            return 'Postponed';
        } if (status === 'cancelled') {
            return 'Cancelled';
        } if (status === 'tba' || status === 'tbat' || status === 'scheduled') {
            return 'vs';
        }

        const ourScore = getOurFinalScore();
        const opponentScore = getOpponentFinalScore();
        if (!homeGame) {
            return `${ourScore} - ${opponentScore}`;
        }
        return `${opponentScore} - ${ourScore}`;
    };

    const getLogos = (side) => {
        if (side === 'left') {
            if (!homeGame) {
                if (primaryBackgroundColor) {
                    if (isValidObject(globalState.silhouetteLogo) && isValidString(globalState.silhouetteLogo.logo2)) {
                        return globalState.silhouetteLogo.logo2;
                    }
                } else if (isValidObject(globalState.logo) && isValidString(globalState.logo.logo2)) {
                    return globalState.logo.logo2;
                }
                return DemoLogo;
            }
            const source = isValidString(opponentLogo) ? opponentLogo : DemoLogo;
            return source;
        }
        if (!homeGame) {
            const source = isValidString(opponentLogo) ? opponentLogo : DemoLogo;
            return source;
        }
        if (primaryBackgroundColor) {
            if (isValidObject(globalState.silhouetteLogo) && isValidString(globalState.silhouetteLogo.logo2)) {
                return globalState.silhouetteLogo.logo2;
            }
        } else if (isValidObject(globalState.logo) && isValidString(globalState.logo.logo2)) {
            return globalState.logo.logo2;
        }

        return '';
    };

    const getTeamNames = (side) => {
        if (side === 'left') {
            if (!homeGame) {
                // not a home game = away game - give home name
                return globalState.teamAlias;
            }
            return opponentName;
        }
        if (!homeGame) {
            return opponentName;
        }
        return globalState.teamAlias;
    };

    const getVenue = () => {
        if (isValidString(venue)) {
            if (venue.length > 20) {
                return `${venue.substr(0, 20)}...`;
            }
            return venue;
        }
        if (homeGame) {
            return 'HOME';
        }

        return 'AWAY';
    };

    const getTimeOfGame = () => {
        if (status === 'tba') {
            return 'TBA';
        } if (status === 'tbat') {
            return getFullDateStringFromDate(getLocalTimeFromDatabaseTime(timeOfGame));
        }
        return `${getFullDateStringFromDate(getLocalTimeFromDatabaseTime(timeOfGame))} ${getTimeInAMPMFromDate(getLocalTimeFromDatabaseTime(timeOfGame))}`;
    };

    return (

        <NavLink key={id} to={`/games/${id}`} className={`game-widget3-container ${css(styles.container)}`} style={{ height: smaller ? '180px' : '250px' }}>
            <Tooltip title={getTeamNames('left')} aria-label={getTeamNames('left')}>
                <img className="game-widget3-logo m-l-30px" src={getLogos('left')} alt={getTeamNames('left')} />
            </Tooltip>
            <div className="game-widget3-details">
                <p className="game-widget3-details-info">{getTimeOfGame()}</p>
                {showGameTickets(globalState, state.tickets, null, scheduled) ? (
                    <Button1
                        title="Tickets"
                        backgroundColor={primaryBackgroundColor ? '#FFFFFF' : globalState.websiteStyles.colors.primaryColor}
                        fontColor={primaryBackgroundColor ? '#000000' : globalState.websiteStyles.colors.fontColor}
                        borderColor={primaryBackgroundColor ? '#000000' : globalState.websiteStyles.colors.fontColor}
                        fontSize="0.7rem"
                        borderRadius="5px"
                        width="100px"
                        height="28px"
                        onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            history.push(`/games/${id}/tickets`);
                        }}
                    />
                ) : <p className="game-widget3-details-status text-uppercase">{getMiddleText()}</p>}
                <p className="game-widget3-details-info text-uppercase">{getVenue()}</p>
            </div>
            <Tooltip title={getTeamNames('right')} aria-label={getTeamNames('right')}>
                <img className="game-widget3-logo m-r-30px" src={getLogos('right')} alt={getTeamNames('right')} />
            </Tooltip>
        </NavLink>
    );
}

GameWidget3.propTypes = {
    id: PropTypes.string.isRequired,
    game: PropTypes.object.isRequired,
    opponentName: PropTypes.string.isRequired,
    opponentLogo: PropTypes.string.isRequired,
    scheduled: PropTypes.bool.isRequired,
    homeGame: PropTypes.bool.isRequired,
    status: PropTypes.string.isRequired,
    timeOfGame: PropTypes.object.isRequired,
    venue: PropTypes.string.isRequired,
    primaryBackgroundColor: PropTypes.bool,
    smaller: PropTypes.bool,
};

GameWidget3.defaultProps = {
    primaryBackgroundColor: false,
    smaller: false,
};

export default memo(GameWidget3);
